"use client"; // Required for client-side interactivity in Next.js 13+

import { useState, useEffect, useRef } from "react";
import { User, LogOut } from "lucide-react";
import { useRouter } from "next/navigation"; // Next.js 13+ uses `next/navigation`
import { signOut, useSession } from "next-auth/react";
import { motion, AnimatePresence } from 'framer-motion';

const ProfileDropdown = ({ isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [imageError, setImageError] = useState(false); // Track if image fails to load
  const dropdownRef = useRef(null); // Ref to track the dropdown container
  const router = useRouter(); // Next.js router for navigation
  const { data: session } = useSession();

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Add event listener when the dropdown is open
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Cleanup the event listener
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]); // Re-run effect when `isOpen` changes

  // Handle image loading errors
  const handleImageError = () => {
    setImageError(true); // Set error state if image fails to load
  };

  // Navigate to the profile page
  const handleProfileClick = () => {
    router.push("/profile"); // Navigate to the /profile page
    setIsOpen(false); // Close the dropdown
  };

  // Handle logout
  const handleLogoutClick = () => {
    localStorage.clear();
    signOut({ callbackUrl: "/signin" });
  };

  // Determine the image source or fallback to the User icon
  const profileImage = session?.user?.image;

  // For mobile view, we'll show a different UI
  if (isMobile) {
    return (
      <div className="w-full">
        <div className="flex flex-col gap-2">
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-2 py-2 w-full text-left hover:bg-gray-100 transition-colors"
          >
            <User className="w-5 h-5 text-gray-600" />
            <span className="font-medium">Profile</span>
          </button>
          <button
            onClick={handleLogoutClick}
            className="flex items-center gap-2 py-2 w-full text-left hover:bg-gray-100 transition-colors"
          >
            <LogOut className="w-5 h-5 text-gray-600" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>
    );
  }

  // Desktop dropdown version
  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Image Button */}
      <button
        onClick={toggleDropdown}
        className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 focus:outline-none"
        aria-label="Toggle profile menu"
      >
        {profileImage && !imageError ? (
          <img
            src={profileImage}
            alt="Profile"
            className="w-full h-full rounded-full object-cover"
            onError={handleImageError} // Triggered if image fails to load
          />
        ) : (
          <User className="w-5 h-5 text-gray-600" /> // Fallback Lucide icon
        )}
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.2 }}
            className="absolute mt-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 right-1/2 lap:right-0 lap:left-auto transform lap:translate-x-0 -translate-x-1/2 w-48"
          >
            <ul className="py-2">
              <li>
                <button
                  onClick={handleProfileClick} // Navigate to /profile
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <User className="w-5 h-5 mr-2" />
                  Profile
                </button>
              </li>
              <li>
                <button
                  onClick={handleLogoutClick} // Handle logout
                  className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <LogOut className="w-5 h-5 mr-2" />
                  Logout
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProfileDropdown;